import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { WidthProvider, Responsive } from 'react-grid-layout';
import { useDispatch, useSelector } from 'react-redux';
import { store as storeNotification } from 'react-notifications-component';
import io from 'socket.io-client';

import ModalAddPanel from '../../components/ModalAddPanel';
import ModalRemoveItem from '../../components/ModalRemoveItem';
import ModalEditTablesDash from '../../components/ModalEditTablesDash';
import ModalEditQueuesDash from '../../components/ModalEditQueuesDash';
import ModalHideItemsDash from '../../components/ModalHideItemsDash';
import { editDashboards } from '../../redux/actions/dashboards';
import DashTable from './DashTable';
import GraphGroupsPerHourIntegerValues from './GraphGroupsPerHourIntegerValues';
import GraphGroupsPerHourPercentValues from './GraphGroupsPerHourPercentValues';
import GraphNetworkGroup from './GraphNetworkGroup';
import GraphGroups from './GraphGroups';
import GraphQueues from './GraphQueues';
import api from '../../api';
import config from '../../api/config.json';

const ResponsiveReactGridLayout = WidthProvider(Responsive);

function GridLayout (props) {
  const notificationConfig = {
    container: 'top-center',
    animationIn: ['animated', 'fadeIn'],
    animationOut: ['animated', 'fadeOut'],
    inset: 'top',
    dismiss: {
      duration: 2000,
      showIcon: false
    }
  };
  const typeReports = {
    TU: 'users',
    TG: 'groupsDay',
    TC: 'calls',
    TON: 'networks'
  };
  const [fieldsReports] = useState({
    users: [],
    calls: [],
    groupsDay: [],
    groupsHour: [],
    networks: []
  });
  const dispatch = useDispatch();
  const dashboards = useSelector(state => state.dashboards);
  const user = useSelector(state => state.authentications.user);
  const dash = useSelector(state => state.dashboards.currentDash);
  const [showEditTable, setShowEditTable] = useState(false);
  const [showHideItemsTable, setShowHideItemsTable] = useState(false);
  const [showEditQueue, setShowEditQueue] = useState(false);
  const [editingPanel, setEditingPanel] = useState();
  const [panelWillRemoved, setPanelWillRemoved] = useState({
    id: '',
    name: '',
    type: ''
  });
  const [showRemovePanel, setShowRemovePanel] = useState(false);
  const [titleModal] = useState('Confirmação');
  const [bodyModal] = useState('Deseja excluir o painel');
  const [nameItemWillRemove, setNameItemWillRemove] = useState('');
  const [requestReports, setRequestReports] = useState({ ...fieldsReports });
  const [responseReports, setResponseReports] = useState({ ...fieldsReports });
  const {
    modalShowChangeStatus,
    setModalShowChangeStatus,
    dataModalChangeStatus,
    setDataModalChangeStatus
  } = props;

  useEffect(() => {
    try {
      const data = {};
      (async function () {
        if (!['M', 'S'].includes(user.type)) {
          data.users = [user._id];
          const networksGroup = await api().get('/api/networks_group');
          const networks =
            networksGroup.data.map(networkGroup => networkGroup._id) || [];
          data.networks = [...networks];
        } else {
          const usrs = await api().get('/api/users');
          const devices = await api().get('/api/devices');
          const networksGroup = await api().get('/api/networks_group');
          const users = usrs.data.map(user => user._id) || [];
          const groups =
            devices.data
              .filter(device => (device.type === 'G' ? device : false))
              .map(device => device.number) || [];
          const extensions =
            devices.data
              .filter(device => (device.type === 'E' ? device : false))
              .map(device => device.number) || [];
          const networks =
            networksGroup.data.map(networkGroup => networkGroup._id) || [];
          data.users = [...users];
          data.groupsDay = [...groups];
          data.groupsHour = [...groups];
          data.calls = [...extensions];
          data.networks = [...networks];
        }
        setRequestReports({ ...fieldsReports, ...data });
      })();
    } catch (err) {
      console.error(err);
    }
  }, [fieldsReports, user.type]);

  useEffect(() => {
    const { protocol, ipServerSKT, portServerSKT } = config;
    const socket = io(`${protocol}://${ipServerSKT}:${portServerSKT}`);
    let requestIsRunning = false;
    let timer = null;

    socket.on('connect', () => {
      console.log('socket dashboard conected ', socket.id);

      requestIsRunning = false;

      timer = setInterval(() => {
        if (socket.connected && !requestIsRunning) {
          requestIsRunning = true;
          socket.emit('requestReports', {
            typeReports: Object.keys(requestReports),
            ...requestReports
          });
        }
      }, 1000);
    });

    socket.on('disconnect', () => {
      console.log('socket dashboard disconnected');
      clearInterval(timer);
    });

    socket.on('responseReports', data => {
      // Atualiza os dados somente se o modal de configuracoes estiver fechado
      if (!showEditTable && !showHideItemsTable && !showEditQueue) {
        // Modifica as proximas requisicoes para usuario operador
        if (!['M', 'S'].includes(user.type)) {
          const { users } = data;
          const calls = users.map(user => user.extension);
          const groups = users.reduce((grps, user) => {
            for (const grp of user.groups) {
              if (!grps.includes(grp.group)) {
                grps.push(grp.group);
              }
            }
            return grps;
          }, []);
          const groupsDay = [...groups];
          const groupsHour = [...groups];
          if (requestReports.calls.length !== calls.length) {
            setRequestReports({
              ...requestReports,
              calls,
              groupsDay,
              groupsHour
            });
          } else {
            for (const number in calls) {
              if (calls[number] !== requestReports.calls[number]) {
                setRequestReports({
                  ...requestReports,
                  calls,
                  groupsDay,
                  groupsHour
                });
                break;
              }
            }
          }
          if (requestReports.groupsDay.length !== groupsDay.length) {
            setRequestReports({
              ...requestReports,
              calls,
              groupsDay,
              groupsHour
            });
          } else {
            for (const number in groupsDay) {
              if (groupsDay[number] !== requestReports.groupsDay[number]) {
                setRequestReports({
                  ...requestReports,
                  calls,
                  groupsDay,
                  groupsHour
                });
                break;
              }
            }
          }
          if (requestReports.groupsHour.length !== groupsHour.length) {
            setRequestReports({
              ...requestReports,
              calls,
              groupsDay,
              groupsHour
            });
          } else {
            for (const number in groupsHour) {
              if (groupsHour[number] !== requestReports.groupsHour[number]) {
                setRequestReports({
                  ...requestReports,
                  calls,
                  groupsDay,
                  groupsHour
                });
                break;
              }
            }
          }
          setResponseReports({ ...data });
        } else {
          setResponseReports({ ...data });
        }
      }
      requestIsRunning = false;
    });

    return () => {
      clearInterval(timer);
      socket.disconnect();
    };
  }, [
    requestReports,
    dispatch,
    showEditTable,
    showHideItemsTable,
    showEditQueue
  ]);

  const onHideItems = i => {
    if (dashboards.enableTransition) {
      storeNotification.addNotification({
        ...notificationConfig,
        title: 'Desabilite a transição para ocultar registros do painel',
        message: ' ',
        type: 'danger'
      });
    } else {
      const editingPanel = dash.items.find(item => item.i === i);
      setEditingPanel({ ...editingPanel });
      if (editingPanel) {
        setShowHideItemsTable(true);
      }
    }
  };

  const onEditItem = i => {
    if (dashboards.enableTransition) {
      storeNotification.addNotification({
        ...notificationConfig,
        title: 'Desabilite a transição para editar o painel',
        message: ' ',
        type: 'danger'
      });
    } else {
      const editingPanel = dash.items.find(item => item.i === i);
      if (editingPanel) {
        if (editingPanel.type === 'GQ') {
          setEditingPanel({ ...editingPanel });
          setShowEditQueue(true);
        } else {
          setEditingPanel({ fields: [], ...editingPanel });
          setShowEditTable(true);
        }
      }
    }
  };

  const onRemoveItem = panel => {
    if (dashboards.enableTransition) {
      storeNotification.addNotification({
        ...notificationConfig,
        title: 'Desabilite a transição para excluir o painel',
        message: ' ',
        type: 'danger'
      });
    } else {
      setPanelWillRemoved({ id: panel.i, name: panel.name, type: panel.type });
      setNameItemWillRemove(panel.name);
      setShowRemovePanel(true);
    }
  };

  const handleCloseRemovePanel = () => setShowRemovePanel(false);

  const handleRemove = () => {
    const updateDash = {
      ...dash,
      items: dash.items.filter(item => item.i !== panelWillRemoved.id)
    };
    dispatch(editDashboards(updateDash));
    setShowRemovePanel(false);
  };

  const memozeidGrid = useMemo(() => {
    return (
      <ResponsiveReactGridLayout
        {...props}
        draggableCancel=".draggable-cancel"
        draggableHandle=".draggable-handle"
      >
        {dash.items.map(el => {
          el.isDraggable = !dashboards.enableTransition;
          el.isResizable = !dashboards.enableTransition;
          const styles = {
            height: `${el.h * 10}px`,
            paddingBottom: '35px',
            marginBottom: '35px',
            overflow: 'auto'
          };
          return (
            <div key={el.i} data-grid={el}>
              <div className="bg-header-grid draggable-handle">
                <div className="col-xs-10 col-sm-10 col-md-10">
                  <span className="text">{el.name}</span>
                </div>
                <div className="col-xs-2 col-sm-2 col-md-2">
                  <button
                    className="remove tooltip-toggle"
                    data-tooltip="Remover"
                    title="Remover"
                    onClick={() => onRemoveItem(el)}
                    onMouseDown={evt => evt.stopPropagation()}
                    onTouchStart={evt => evt.stopPropagation()}
                  >
                    <i className="fa fa-times icon-size" />
                  </button>
                  <button
                    className={`edit tooltip-toggle ${
                      !['TU', 'TG', 'TC', 'TON', 'GQ'].includes(el.type)
                        ? 'tooltip-toggle-disabled'
                        : ''
                    }`}
                    data-tooltip="Editar"
                    title="Editar"
                    onClick={() => onEditItem(el.i)}
                    onMouseDown={evt => evt.stopPropagation()}
                    onTouchStart={evt => evt.stopPropagation()}
                    disabled={
                      !['TU', 'TG', 'TC', 'TON', 'GQ'].includes(el.type)
                    }
                  >
                    <i className="fa fa-pen icon-size" />
                  </button>
                  <button
                    className="hide-items tooltip-toggle"
                    data-tooltip="Ocultar Registros"
                    title="Configurar Exibição"
                    onClick={() => onHideItems(el.i)}
                    onMouseDown={evt => evt.stopPropagation()}
                    onTouchStart={evt => evt.stopPropagation()}
                  >
                    <i className="fa fa-list icon-size" />
                  </button>
                </div>
              </div>
              <div id={`panel_${el.i}`} className="draggable-cancel">
                {['TU', 'TG', 'TC', 'TON'].includes(el.type) && (
                  <div style={{ ...styles }}>
                    <div
                      className="table-responsive"
                      style={{ marginBottom: '35px' }}
                    >
                      <DashTable
                        el={el}
                        reports={responseReports[typeReports[el.type]] || []}
                        dash={dash}
                        sessionUser={user}
                        modalShowChangeStatus={
                          ['M', 'S'].includes(user.type)
                            ? modalShowChangeStatus
                            : false
                        }
                        setModalShowChangeStatus={
                          ['M', 'S'].includes(user.type)
                            ? setModalShowChangeStatus
                            : () => {}
                        }
                        dataModalChangeStatus={
                          ['M', 'S'].includes(user.type)
                            ? dataModalChangeStatus
                            : {}
                        }
                        setDataModalChangeStatus={
                          ['M', 'S'].includes(user.type)
                            ? setDataModalChangeStatus
                            : () => {}
                        }
                      />
                    </div>
                  </div>
                )}
                {['GGH'].includes(el.type) && (
                  <div style={{ ...styles }}>
                    <GraphGroupsPerHourIntegerValues
                      el={el}
                      reports={responseReports.groupsHour || []}
                    />
                  </div>
                )}
                {['GGHP'].includes(el.type) && (
                  <div style={{ ...styles }}>
                    <GraphGroupsPerHourPercentValues
                      el={el}
                      reports={responseReports.groupsHour || []}
                    />
                  </div>
                )}
                {['GON'].includes(el.type) && (
                  <div style={{ ...styles }}>
                    <GraphNetworkGroup
                      el={el}
                      reports={responseReports.networks || []}
                    />
                  </div>
                )}
                {['GG'].includes(el.type) && (
                  <div style={{ ...styles }}>
                    <GraphGroups
                      el={el}
                      reports={responseReports.groupsDay || []}
                    />
                  </div>
                )}
                {['GQ'].includes(el.type) && (
                  <div style={{ ...styles }}>
                    <GraphQueues
                      el={el}
                      reports={responseReports.groupsDay || []}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </ResponsiveReactGridLayout>
    );
  }, [dash, responseReports]);

  return (
    <div className="container-fluid">
      <ModalAddPanel />
      <ModalHideItemsDash
        showHideItemsTable={showHideItemsTable}
        setShowHideItemsTable={setShowHideItemsTable}
        currentDash={dash}
        editingPanel={editingPanel}
      />
      <ModalEditTablesDash
        showEditTable={showEditTable}
        setShowEditTable={setShowEditTable}
        currentDash={dash}
        editingPanel={editingPanel}
      />
      <ModalEditQueuesDash
        showEditQueue={showEditQueue}
        setShowEditQueue={setShowEditQueue}
        currentDash={dash}
        editingPanel={editingPanel}
      />
      <ModalRemoveItem
        showModal={showRemovePanel}
        handleModalClose={handleCloseRemovePanel}
        handleModalConfirmYes={handleRemove}
        titleModal={titleModal}
        bodyModal={bodyModal}
        nameItemWillRemove={nameItemWillRemove}
      />
      {memozeidGrid}
    </div>
  );
}

GridLayout.defaultProps = {
  className: 'layout',
  compactType: 'vertical',
  mounted: false,
  cols: { lg: 120, md: 120, sm: 120, xs: 20, xxs: 20 },
  rowHeight: 1
};

export default GridLayout;

GridLayout.propTypes = {
  modalShowChangeStatus: PropTypes.bool,
  setModalShowChangeStatus: PropTypes.func,
  dataModalChangeStatus: PropTypes.object,
  setDataModalChangeStatus: PropTypes.func
};
